import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Button, Space, Tag, message, Spin, Pagination } from 'antd';
import { CameraOutlined, ReloadOutlined, WifiOutlined, ApiOutlined } from '@ant-design/icons';
import { clientApi } from '../services/api';
import config from '../config';

export default function MonitorPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const containerRef = useRef(null);
  const cardW = 220, gap = 12;

  const calcPageSize = useCallback(() => {
    if (!containerRef.current) return 8;
    const w = containerRef.current.offsetWidth;
    const perRow = Math.max(1, Math.floor((w + gap) / (cardW + gap)));
    return perRow * 2;
  }, []);

  const [pageSize, setPageSize] = useState(8);

  useEffect(() => {
    const update = () => setPageSize(calcPageSize());
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [calcPageSize]);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientsRes, shotsRes] = await Promise.all([
        clientApi.getOnline(),
        clientApi.getLatestScreenshots(),
      ]);
      const allClients = clientsRes.data || [];
      const onlineClients = allClients.filter(c => c.status === 'online');
      const shotMap = {};
      (shotsRes.data || []).forEach(s => { shotMap[s.client_id] = s; });
      setItems(onlineClients.map(c => ({
        ...c,
        screenshot: shotMap[c.id],
      })));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const sendAllScreenshots = async () => {
    message.loading({ content: '正在发送截图指令...', key: 'ss' });
    try {
      const results = await Promise.allSettled(
        items.filter(c => c.status === 'online').map(c => clientApi.triggerScreenshot(c.id))
      );
      const ok = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      message.destroy('ss');
      message.success(`截图指令已发送 ${ok}/${items.length}`);
      setTimeout(loadData, 3000);
    } catch { message.destroy('ss'); message.error('发送失败'); }
  };

  const handleScreenshot = async (id) => {
    message.loading({ content: '发送中...', key: id });
    try {
      const r = await clientApi.triggerScreenshot(id);
      message.destroy(id);
      if (r.success) { message.success('截图指令已发送'); setTimeout(loadData, 2000); }
      else message.error(r.message || '失败');
    } catch { message.destroy(id); message.error('发送失败'); }
  };

  const pagedItems = items.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Card title="客户端监控" size="small"
      extra={<Space><Button size="small" icon={<CameraOutlined />} onClick={sendAllScreenshots}>全部截图</Button><Button size="small" icon={<ReloadOutlined />} onClick={loadData}>刷新</Button></Space>}>
      <Spin spinning={loading}>
        <div ref={containerRef} style={{ display: 'flex', flexWrap: 'wrap', gap }}>
          {pagedItems.map(c => {
            const isOnline = c.status === 'online';
            return (
              <Card key={c.id} size="small" style={{ width: cardW }}
                title={<Space size={4}>{isOnline ? <Tag color="green" icon={<WifiOutlined />}>在线</Tag> : <Tag color="default" icon={<ApiOutlined />}>离线</Tag>} <span style={{ fontSize: 13 }}>{c.name || c.id}</span></Space>}
                extra={<Button size="small" icon={<CameraOutlined />} onClick={() => { if (isOnline) handleScreenshot(c.id); else message.warning('离线客户端无法截图'); }} />}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
                  <div>IP: {c.client_addr || '-'} <Tag style={{ fontSize: 10 }}>{c.version || '-'}</Tag></div>
                  <div>最后在线: {c.last_seen || '-'}</div>
                  {c.screenshot && <div>截图: {new Date(c.screenshot.created_at).toLocaleString()}</div>}
                </div>
                {c.screenshot
                  ? <img src={`${config.API_URL}/screenshots/file/${c.screenshot.id}`}
                      alt="截图" style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 4, border: '1px solid #e8e8e8', cursor: 'pointer' }}
                      onClick={() => window.open(`${config.API_URL}/screenshots/file/${c.screenshot.id}`, '_blank')} />
                  : <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', background: '#fafafa', borderRadius: 4 }}>暂无截图</div>}
              </Card>
            );
          })}
          {items.length === 0 && !loading && <div style={{ padding: 40, color: '#999' }}>暂无客户端</div>}
        </div>
        {items.length > pageSize && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Pagination current={page} pageSize={pageSize} total={items.length} onChange={setPage} size="small" showTotal={t => `共 ${t} 个客户端`} />
          </div>
        )}
      </Spin>
    </Card>
  );
}
